import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  useDataProvider,
  useGetIdentity,
  useGetOne,
  useNotify,
} from 'react-admin';
import { Eye, Loader2 } from 'lucide-react';

import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { cn } from '@/lib/utils';

import { CustomerEventType, Funeral } from '../../types';
import { getToken } from '../../authentication/authProvider';
import { CaseDocument, fetchCaseDocuments } from './dispatchDocuments';
import {
  buildPartnerDocsBody,
  buildPartnerDocsSubject,
  PartnerDocsEmailFacts,
} from './notificationTemplates';
import {
  docsLabel,
  PartnerDocItem,
  PartnerRole,
} from './partnerDocRequirements';
import { announcePartnerDocsChanged } from './partnerDocsApi';
import { usePartnerDocsStatus } from './usePartnerDocsStatus';

const API_BASE = (() => {
  const url =
    process.env.REACT_APP_PAPERMAN_API_URL || 'http://localhost:3001/';
  return url.endsWith('/') ? url : `${url}/`;
})();

export interface PartnerDocsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  funeral: Funeral;
  mode: 'send' | 'request';
  /** All of ONE partner's items (the dialog offers the ones that fit the mode). */
  items: PartnerDocItem[];
  /** Pre-select only these document types (defaults: every unsent one). */
  documentTypes?: string[];
  onSent?: () => void;
}

export const capitalize = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);

const canSend = (i: PartnerDocItem) => i.defaultDirection === 'outbound';

/**
 * CREMATORIUM_DOCS_FOLLOWUP_PLAN §5: send the documents a partner is missing,
 * or ask a partner for one they hold. Goes through the same
 * POST /funerals/:id/partner-email as the dispatch composer, with partnerId and
 * typed attachments, so a successful send records the case_partner_documents
 * rows server-side; logs the same EMAIL customer event.
 */
export const PartnerDocsDialog: React.FC<PartnerDocsDialogProps> = ({
  open,
  onOpenChange,
  funeral,
  mode,
  items,
  documentTypes,
  onSent,
}) => {
  const notify = useNotify();
  const dataProvider = useDataProvider();
  const { identity } = useGetIdentity();
  const partner = items[0];
  // EN/FR toggle for Quebec partners only (Q3); Ontario is English only.
  const isQuebec = (partner?.partnerRegion ?? funeral.region) === 'quebec';

  const { data: deceased } = useGetOne(
    'clients',
    { id: funeral.deceased_client_id },
    { enabled: open && !!funeral.deceased_client_id }
  );
  const { data: transport } = useGetOne(
    'partners',
    { id: funeral.transport_partner_id as string },
    { enabled: open && !!funeral.transport_partner_id }
  );

  const offered = useMemo(
    () =>
      mode === 'send'
        ? items.filter(canSend)
        : items.filter((i) => i.defaultDirection === 'inbound'),
    [items, mode]
  );

  const [docs, setDocs] = useState<Record<string, CaseDocument | null>>({});
  const [selected, setSelected] = useState<string[]>([]);
  const [lang, setLang] = useState<'en' | 'fr'>('en');
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [subjectEdited, setSubjectEdited] = useState(false);
  const [bodyEdited, setBodyEdited] = useState(false);
  const [sending, setSending] = useState(false);

  // Load what's on file and pick the default selection each time it opens.
  useEffect(() => {
    if (!open) return;
    setSubjectEdited(false);
    setBodyEdited(false);
    setLang('en');
    let cancelled = false;
    void fetchCaseDocuments(
      funeral.id,
      offered.map((i) => i.documentType)
    ).then((d) => {
      if (cancelled) return;
      setDocs(d);
      const want = documentTypes?.length ? documentTypes : null;
      setSelected(
        offered
          .filter((i) => (mode === 'request' ? true : !!d[i.documentType]))
          .filter((i) => (want ? want.includes(i.documentType) : !i.row))
          .map((i) => i.documentType)
      );
    });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, funeral.id, mode]);

  const deceasedName =
    `${deceased?.first_name || ''} ${deceased?.last_name || ''}`.trim() ||
    deceased?.name ||
    '';
  const dispatched = (funeral as { transport_dispatched_date?: string | null })
    .transport_dispatched_date;
  const facts: PartnerDocsEmailFacts = {
    funeralId: funeral.id,
    deceasedName,
    deceasedDob: deceased?.birthdate
      ? String(deceased.birthdate).slice(0, 10)
      : '',
    dispatchDate: dispatched
      ? new Date(dispatched).toLocaleDateString(
          lang === 'fr' ? 'fr-CA' : 'en-CA',
          {
            month: 'short',
            day: 'numeric',
            year: 'numeric',
          }
        )
      : '',
    sp3Number: funeral.sp3_number || '',
    transportName: transport?.name || '',
    isQuebec,
  };

  const chosen = offered.filter((i) => selected.includes(i.documentType));

  // Regenerate subject/body from the selection unless staff edited them.
  useEffect(() => {
    if (!open) return;
    if (!subjectEdited) setSubject(buildPartnerDocsSubject(facts));
    if (!bodyEdited) {
      setBody(
        buildPartnerDocsBody(mode, lang, facts, {
          label: docsLabel(chosen.length ? chosen : offered.slice(0, 1), lang),
          count: Math.max(chosen.length, 1),
          resend: chosen.length > 0 && chosen.every((i) => !!i.row),
        })
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, selected.join(','), lang, deceasedName, transport?.name, mode]);

  if (!partner) return null;
  const emails = (partner.partnerEmail || '')
    .split(',')
    .map((e) => e.trim())
    .filter(Boolean);
  const hasEmail = emails.length > 0;
  const role = partner.roles[0];

  const handleSend = async () => {
    if (!hasEmail) return;
    if (mode === 'send' && chosen.length === 0) return;
    setSending(true);
    try {
      // Fresh signed URLs right before sending.
      const fresh =
        mode === 'send'
          ? await fetchCaseDocuments(
              funeral.id,
              chosen.map((i) => i.documentType)
            )
          : {};
      const attachments = chosen
        .filter(() => mode === 'send')
        .map((i) => ({
          url: fresh[i.documentType]?.url || '',
          kind: i.documentType,
        }));
      if (attachments.some((a) => !a.url))
        throw new Error('A selected document is no longer on file');

      const token = await getToken();
      const response = await fetch(
        `${API_BASE}funerals/${funeral.id}/partner-email`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            to: emails,
            subject,
            body,
            partnerName: partner.partnerName,
            role,
            heading: 'Documents',
            partnerId: partner.partnerId,
            deceasedName,
            actorName: identity?.fullName,
            attachments,
          }),
        }
      );
      const data = await response.json().catch(() => ({}));
      if (!response.ok)
        throw new Error(data.message || data.error || 'Failed to send email');

      await dataProvider.create('customer_events', {
        data: {
          funeral_id: Number(funeral.id),
          timestamp: new Date().toISOString(),
          user_id: identity?.id ? Number(identity.id) : undefined,
          user_name: identity?.fullName || 'System',
          type: CustomerEventType.EMAIL,
          recipient_type: role,
          recipient_id: Number(partner.partnerId),
          recipient_name: partner.partnerName,
          contact_method: 'email',
          contact_detail: emails.join(', '),
          subject,
          message: body,
        },
      });

      notify(
        mode === 'send'
          ? `Sent ${docsLabel(chosen, 'en')} to ${partner.partnerName}`
          : `Asked ${partner.partnerName} for ${docsLabel(
              chosen.length ? chosen : offered,
              'en'
            )}`,
        { type: 'success' }
      );
      announcePartnerDocsChanged(funeral.id);
      onSent?.();
      onOpenChange(false);
    } catch (error) {
      notify(
        `Email not sent: ${
          error instanceof Error ? error.message : 'Unknown error'
        }`,
        {
          type: 'error',
        }
      );
    } finally {
      setSending(false);
    }
  };

  return (
    <span onClick={(e) => e.stopPropagation()}>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="cleo-ui sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>
              {mode === 'send' ? 'Send documents to' : 'Request documents from'}{' '}
              {partner.partnerName}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-3">
            <div className="text-sm">
              <span className="text-muted-foreground">To: </span>
              {hasEmail ? (
                emails.join(', ')
              ) : (
                <span className="text-amber-700">
                  No email on file for {partner.partnerName}, so Cleo can&apos;t
                  send this.{' '}
                  <Link
                    className="underline"
                    to={`/partners/${partner.partnerId}`}
                  >
                    Add it on the partner&apos;s page
                  </Link>
                  , or send it outside Cleo and use Mark as sent.
                </span>
              )}
            </div>

            {isQuebec && (
              <div className="flex gap-1">
                {(['en', 'fr'] as const).map((l) => (
                  <Button
                    key={l}
                    type="button"
                    size="sm"
                    variant={lang === l ? 'secondary' : 'outline'}
                    onClick={() => setLang(l)}
                  >
                    {l.toUpperCase()}
                  </Button>
                ))}
              </div>
            )}

            {mode === 'send' && (
              <div className="space-y-2 rounded-md border bg-background p-3">
                <p className="text-sm font-medium">Attachments</p>
                {offered.map((i) => {
                  const doc = docs[i.documentType];
                  return (
                    <div
                      key={i.documentType}
                      className="flex items-center justify-between gap-2"
                    >
                      <label className="flex items-center gap-2 text-sm">
                        <input
                          type="checkbox"
                          className="h-4 w-4 accent-[#5e35b1]"
                          checked={!!doc && selected.includes(i.documentType)}
                          disabled={!doc}
                          onChange={(e) =>
                            setSelected((prev) =>
                              e.target.checked
                                ? [...prev, i.documentType]
                                : prev.filter((t) => t !== i.documentType)
                            )
                          }
                        />
                        <span className={cn(!doc && 'text-muted-foreground')}>
                          {capitalize(i.label.en)}
                        </span>
                        {i.row && (
                          <span className="text-xs text-muted-foreground">
                            (sent{' '}
                            {new Date(i.row.satisfied_at).toLocaleDateString()})
                          </span>
                        )}
                      </label>
                      {doc ? (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => window.open(doc.url, '_blank')}
                        >
                          <Eye className="mr-1.5 h-4 w-4" /> Preview
                        </Button>
                      ) : (
                        <Badge variant="outline" className="text-amber-600">
                          Missing
                        </Badge>
                      )}
                    </div>
                  );
                })}
              </div>
            )}

            <div className="space-y-1.5">
              <Label htmlFor="partner-docs-subject">Subject</Label>
              <Input
                id="partner-docs-subject"
                value={subject}
                onChange={(e) => {
                  setSubject(e.target.value);
                  setSubjectEdited(true);
                }}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="partner-docs-body">Message</Label>
              <Textarea
                id="partner-docs-body"
                rows={10}
                value={body}
                onChange={(e) => {
                  setBody(e.target.value);
                  setBodyEdited(true);
                }}
              />
            </div>

            <div className="flex justify-end gap-2">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => onOpenChange(false)}
                disabled={sending}
              >
                Cancel
              </Button>
              <Button
                size="sm"
                onClick={() => void handleSend()}
                disabled={
                  sending ||
                  !hasEmail ||
                  (mode === 'send' && chosen.length === 0)
                }
              >
                {sending && <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />}
                {mode === 'send' ? 'Send' : 'Send request'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </span>
  );
};

export interface PartnerDocsDialogLauncherProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  funeralId: string | number;
  /** Which partner: by id, or by the role it plays on the case. */
  partnerId?: string;
  role?: PartnerRole;
  /** Defaults to 'send' when the partner has anything we can send, else 'request'. */
  mode?: 'send' | 'request';
  /** Pre-select these document types (e.g. the missing ones); default = every unsent one. */
  documentTypes?: string[];
  onSent?: () => void;
}

/**
 * Open PartnerDocsDialog from anywhere with just the case and the partner (for
 * example a partner chip in the feed composer): loads the funeral and the
 * case's partner-document status itself. Renders nothing until both are in,
 * or if that partner has no document requirements on the case.
 */
export const PartnerDocsDialogLauncher: React.FC<
  PartnerDocsDialogLauncherProps
> = ({
  open,
  onOpenChange,
  funeralId,
  partnerId,
  role,
  mode,
  documentTypes,
  onSent,
}) => {
  const { data: funeral } = useGetOne<Funeral>(
    'funerals',
    { id: funeralId },
    { enabled: open }
  );
  const { status } = usePartnerDocsStatus(funeralId, open);
  if (!open || !funeral || !status) return null;
  const items = status.items.filter((i) =>
    partnerId
      ? i.partnerId === String(partnerId)
      : !!role && i.roles.includes(role)
  );
  if (items.length === 0) return null;
  const partnerItems = status.items.filter(
    (i) => i.partnerId === items[0].partnerId
  );
  const resolvedMode =
    mode ??
    (partnerItems.some((i) => canSend(i) && i.onFile) ? 'send' : 'request');
  return (
    <PartnerDocsDialog
      open={open}
      onOpenChange={onOpenChange}
      funeral={funeral}
      mode={resolvedMode}
      items={partnerItems}
      documentTypes={documentTypes}
      onSent={onSent}
    />
  );
};
